import { viewerEvents } from '@/atoms/viewer.atom';
import { DataTable, type FiltersConfig, type FiltersState } from '@/components/DataTable';
import FullPageError from '@/components/FullPageError';
import PortalTooltip from '@/components/PortalTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { propertyBlacklist } from '@/core/config';
import { formatPropertyValue } from '@/lib/formatPropertyValue';
import { execute } from '@/graphql/execute';
import { ExportType, LayersQuery } from '@/graphql/graphql';
import { useDebounce } from '@/hooks/useDebounce';
import { useOrganizations } from '@/hooks/useOrganizations';
import { analytics } from '@/lib/analytics';
import {
  deleteFeaturesMutation,
  downloadFeaturesMutation,
  featureQuery,
  featuresPaginatedQuery,
  featureSummaryQuery,
} from '@/queries/feature.query';
import { layerPropertyValuesQuery } from '@/queries/layer.query';
import { DangerTriangleSolid, Download, InfoCircleSolid, LayersThreeSolid } from '@mynaui/icons-react';
import { ErrorBoundary } from '@sentry/react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';
import { ChevronDown, Crosshair, Loader2, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BulkEditDrawer } from './BulkEditDrawer';

interface iTableProps {
  projectId: string;
  layerId: string;
  selection?: string[];
  onSelect?: (selection: string[]) => void;
  selectionKey?: string;
  layers: LayersQuery['layers'];
}
export const Table = ({ projectId, layerId, selection, onSelect, selectionKey, layers }: iTableProps) => {
  const currentLayer = layers.find((l) => l.id === layerId);
  const isVectorLayer = currentLayer?.type === 'VECTOR';

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<FiltersState>({});
  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebounce(() => searchInput.trim(), [searchInput], 300);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ feature: any; x: number; y: number } | null>(null);
  const [contextEditFeatureId, setContextEditFeatureId] = useState<string | null>(null);
  const [deleteConfirmFeature, setDeleteConfirmFeature] = useState<{ id: string } | null>(null);
  const [jumpingFeatureId, setJumpingFeatureId] = useState<string | null>(null);
  const { hasRole } = useOrganizations();
  const queryClient = useQueryClient();
  const { t } = useTranslation('project');

  const hasSelection = selection && selection.length > 0;

  const downloadMutation = useMutation({
    mutationFn: async ({ exportType }: { exportType: ExportType }) => {
      return execute(downloadFeaturesMutation, {
        ids: hasSelection ? selection : undefined,
        layerId: hasSelection ? undefined : layerId,
        exportType,
      });
    },
    onMutate: () => {
      return { toastId: toast.loading(t('attributeTable.downloadingFeatures')) };
    },
    onSuccess: (data, variables, context) => {
      const url = (data as any)?.downloadFeatures;
      if (url) {
        analytics.capture('Features Exported', { exportType: variables.exportType, count: selection?.length || 'all' });
        window.open(url, '_blank');
        toast.success(t('attributeTable.downloadReady'), { id: context?.toastId });
      }
    },
    onError: (error: Error, _, context) => {
      toast.error(error.message || t('attributeTable.failedDownloadFeatures'), { id: context?.toastId });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!hasSelection) return;
      return execute(deleteFeaturesMutation, { ids: selection });
    },
    onMutate: () => {
      return { toastId: toast.loading(t('attributeTable.deletingFeatures')) };
    },
    onSuccess: (_data, _vars, context) => {
      const count = selection?.length || 0;
      analytics.capture('Features Deleted', { count });
      toast.success(t('attributeTable.featuresDeleted', { count }), { id: context?.toastId });
      onSelect?.([]);
      queryClient.invalidateQueries({ queryKey: ['features-paginated', projectId, layerId] });
      queryClient.invalidateQueries({ queryKey: ['features', layerId] });
      queryClient.invalidateQueries({ queryKey: ['layer-property-values', layerId] });
    },
    onError: (error: Error, _vars, context) => {
      toast.error(error.message || t('attributeTable.failedDeleteFeatures'), { id: context?.toastId });
    },
  });

  const contextDeleteMutation = useMutation({
    mutationFn: async (featureId: string) => {
      return execute(deleteFeaturesMutation, { ids: [featureId] });
    },
    onMutate: () => {
      return { toastId: toast.loading(t('attributeTable.deletingFeature')) };
    },
    onSuccess: (_data, featureId, context) => {
      analytics.capture('Feature Deleted');
      toast.success(t('attributeTable.featureDeleted'), { id: context?.toastId });
      if (selection?.includes(featureId)) {
        onSelect?.(selection.filter((id) => id !== featureId));
      }
      queryClient.invalidateQueries({ queryKey: ['features-paginated', projectId, layerId] });
      queryClient.invalidateQueries({ queryKey: ['features', layerId] });
      queryClient.invalidateQueries({ queryKey: ['layer-property-values', layerId] });
    },
    onError: (error: Error, _vars, context) => {
      toast.error(error.message || t('attributeTable.failedDeleteFeature'), { id: context?.toastId });
    },
  });

  const handleJumpToFeature = async (featureId: string) => {
    setJumpingFeatureId(featureId);
    try {
      const data = await execute(featureQuery, { id: featureId });
      const geometry = (data as any)?.feature?.geometry;
      if (!geometry?.coordinates) return;

      // Compute centroid from geometry coordinates
      const flatCoords: [number, number, number][] = [];
      const flatten = (val: any) => {
        if (Array.isArray(val) && typeof val[0] === 'number') {
          flatCoords.push(val as [number, number, number]);
        } else if (Array.isArray(val)) {
          val.forEach(flatten);
        }
      };
      flatten(geometry.coordinates);

      if (flatCoords.length === 0) return;
      const cx = flatCoords.reduce((s, p) => s + p[0], 0) / flatCoords.length;
      const cy = flatCoords.reduce((s, p) => s + p[1], 0) / flatCoords.length;
      const cz = flatCoords.reduce((s, p) => s + (p[2] || 0), 0) / flatCoords.length;
      viewerEvents.emit('jumpToTarget', { x: cx, y: cy, z: cz });
    } catch {
      toast.error(t('attributeTable.failedJumpToFeature'));
    } finally {
      setJumpingFeatureId(null);
    }
  };

  useEffect(() => {
    setFilters({});
    setSearchInput('');
  }, [layerId]);

  const orderBy = useMemo(() => {
    if (sorting.length === 0) return undefined;
    const sort = sorting[0];
    const direction: 'ASC' | 'DESC' = sort.desc ? 'DESC' : 'ASC';

    return { field: sort.id, direction };
  }, [sorting]);

  const { data: propertyValuesData } = useQuery({
    queryKey: ['layer-property-values', layerId],
    queryFn: () => execute(layerPropertyValuesQuery, { layerId }),
    enabled: !!layerId && isVectorLayer,
  });

  const backendFilters = useMemo(() => {
    const result: Array<{ key: string; values: string[] }> = [];
    for (const [columnId, filterValue] of Object.entries(filters)) {
      if (filterValue.type === 'options' && filterValue.values.length > 0) {
        const key = columnId.replace('properties.', '');
        result.push({ key, values: filterValue.values });
      }
    }
    return result.length > 0 ? result : undefined;
  }, [filters]);

  const { data: summaryData } = useQuery({
    queryKey: ['feature-summary', layerId, backendFilters, searchQuery],
    queryFn: () =>
      execute(featureSummaryQuery, {
        layerId,
        filters: backendFilters,
        query: searchQuery || undefined,
      }),
    enabled: !!layerId && isVectorLayer,
  });

  const summary = (summaryData as any)?.featureSummary;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ['features-paginated', projectId, layerId, orderBy, backendFilters, searchQuery],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      execute(featuresPaginatedQuery, {
        projectId,
        layerId: layerId || undefined,
        cursor: pageParam || undefined,
        limit: 100,
        orderBy: orderBy || undefined,
        filters: backendFilters,
        query: searchQuery || undefined,
      }),
    getNextPageParam: (lastPage) => {
      const cursor = (lastPage as any)?.featuresPaginated?.cursor;
      return cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!projectId && !!layerId && isVectorLayer,
    placeholderData: keepPreviousData,
  });

  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newSorting);
  };

  const features = data?.pages.flatMap((page) => (page as any)?.featuresPaginated?.features || []) || [];

  // Build property aliases from the layer ancestry (root -> child) so the
  // closest (child-most) alias wins on duplicates. Mirrors FeatureProperties
  // and BulkEditDrawer so the table header shows the same labels.
  const aliases = useMemo(() => {
    if (!currentLayer) return {} as Record<string, string>;

    const getAncestry = (current: typeof currentLayer | undefined): typeof currentLayer[] => {
      if (!current?.parentLayerId) return [];
      const parent = layers.find((l) => l.id === current.parentLayerId);
      if (!parent) return [];
      return [parent, ...getAncestry(parent)];
    };

    return [...getAncestry(currentLayer).reverse(), currentLayer]
      .flatMap((l) => l.propertyAliases || [])
      .reduce(
        (acc, alias) => {
          acc[alias.property] = alias.alias;
          return acc;
        },
        {} as Record<string, string>,
      );
  }, [currentLayer, layers]);

  const getPropertyName = (property: string) => aliases[property] || property;

  const columns = useMemo(
    () =>
      Object.keys(features[0]?.properties || {})
        .filter((key) => !propertyBlacklist.includes(key))
        .map((key) => ({
          id: `properties.${key}`,
          header: getPropertyName(key),
          accessorKey: `properties.${key}`,
          // Without a custom `cell`, react-table coerces nested object/array
          // values to the literal string `[object Object]` (matters for
          // stockpile features whose `result` / `mass` / `basePlane` /
          // `material` properties are objects). `formatPropertyValue`
          // unwraps those to JSON; scalars pass through unchanged.
          cell: ({ getValue }: { getValue: () => unknown }) => formatPropertyValue(getValue()),
        })),
    [features[0]?.properties, aliases],
  );

  const filtersConfig = useMemo<FiltersConfig>(() => {
    const config: FiltersConfig = {};
    const propertyValues = (propertyValuesData as any)?.layerPropertyValues || [];

    columns.forEach((col) => {
      const propertyKey = col.id.replace('properties.', '');
      const pv = propertyValues.find((p: any) => p.property === propertyKey);

      if (pv && pv.values.length > 0) {
        config[col.id] = {
          type: 'options',
          options: pv.values.map((v: string) => ({ label: v, value: v })),
        };
      } else {
        config[col.id] = { type: 'text' };
      }
    });
    return config;
  }, [columns, propertyValuesData]);

  if (isError) return <FullPageError title={t('attributeTable.errorLoadingAttributes')} error={error as Error} />;

  if (!currentLayer) {
    return (
      <div className='flex flex-col items-center justify-center h-full'>
        <LayersThreeSolid className='size-10 text-muted-foreground/30' />
        <p className='text-sm font-medium text-muted-foreground mt-3'>{t('attributeTable.noLayerSelected')}</p>
        <p className='text-sm text-muted-foreground/60 mt-1'>{t('attributeTable.noLayerSelectedDescription')}</p>
      </div>
    );
  }

  if (!isVectorLayer) {
    return (
      <div className='flex flex-col items-center justify-center h-full'>
        <DangerTriangleSolid className='size-10 text-amber-400/70' />
        <p className='text-sm font-medium text-muted-foreground mt-3'>{t('attributeTable.nonVectorLayer')}</p>
        <p className='text-sm text-muted-foreground/60 mt-1 max-w-sm text-center'>
          {t('attributeTable.nonVectorLayerDescription')}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className='p-4 pb-0 h-full flex flex-col flex-1'>
        <div className='flex-1 flex flex-col select-none'>
          <ErrorBoundary
            fallback={
              <div className='flex flex-col items-center justify-center px-4'>
                {t('attributeTable.failedLoadLayers')}
              </div>
            }>
            <div className='flex items-center mb-4'>
              <Input
                className='w-64 mr-auto'
                placeholder={t('attributeTable.searchFeatures')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <div className='flex items-center gap-2'>
                {hasSelection && hasRole('EDITOR') && (
                  <Button variant='outline' className='h-9' onClick={() => setBulkEditOpen(true)}>
                    <Pencil className='size-4' />
                    {t('attributeTable.edit')}
                  </Button>
                )}
                {hasSelection && hasRole('ADMIN') && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant='destructive' className='h-9' disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? (
                          <Loader2 className='size-4 animate-spin' />
                        ) : (
                          <Trash2 className='size-4' />
                        )}
                        {t('attributeTable.delete')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('attributeTable.confirmDeleteTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('attributeTable.confirmDeleteFeatures', { count: selection.length })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('attributeTable.cancel')}</AlertDialogCancel>
                        <AlertDialogAction variant='destructive' onClick={() => deleteMutation.mutate()}>
                          {t('attributeTable.delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <div className='flex'>
                  <Button
                    variant='outline'
                    className='rounded-r-none h-9'
                    disabled={downloadMutation.isPending}
                    onClick={() => downloadMutation.mutate({ exportType: 'CSV' as ExportType })}>
                    {downloadMutation.isPending ? (
                      <Loader2 className='size-4 animate-spin' />
                    ) : (
                      <Download className='size-4' />
                    )}
                    {hasSelection ? t('attributeTable.downloadFeatures') : t('attributeTable.downloadAllFeatures')}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='outline'
                        className='rounded-l-none border-l-0 px-2.5!'
                        disabled={downloadMutation.isPending}>
                        <ChevronDown className='size-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem onClick={() => downloadMutation.mutate({ exportType: 'CSV' as ExportType })}>
                        {t('attributeTable.downloadAsCsv')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => downloadMutation.mutate({ exportType: 'GEOJSON' as ExportType })}>
                        {t('attributeTable.downloadAsGeojson')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
            <DataTable
              total={(data?.pages[0] as any)?.featuresPaginated?.total || 0}
              columns={[
                {
                  id: 'serial',
                  header: (
                    <span className='flex items-center gap-1'>
                      <InfoCircleSolid
                        data-tooltip-id='attribute-table-serial-tooltip'
                        className='size-3.5 opacity-60'
                      />
                      {t('attributeTable.sgid')}
                    </span>
                  ) as any,
                  enableSorting: false,
                  accessorKey: 'serial',
                },
                ...columns,
              ]}
              data={features}
              loading={isLoading}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              onLoadMore={() => fetchNextPage().then(() => {})}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              selection={selection}
              onSelect={onSelect}
              selectionKey={selectionKey}
              filtersConfig={filtersConfig}
              filters={filters}
              onFilterChange={setFilters}
              highlightText={searchQuery || undefined}
              onRowContextMenu={(event, feature) => {
                setContextMenu({ feature, x: event.clientX, y: event.clientY });
              }}
            />
          </ErrorBoundary>
        </div>
        <div className='flex items-center justify-between gap-4 py-4'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-medium truncate max-w-48'>{currentLayer?.name}</span>
            {isPlaceholderData && isFetching && <Loader2 className='size-4 animate-spin text-muted-foreground' />}
          </div>
          <div className='flex items-center gap-4'>
            {selection && selection.length > 0 && (
              <span className='text-sm font-[450]'>
                {t('attributeTable.selectedFeatures', { count: selection.length })}
              </span>
            )}
            <span className='text-sm text-muted-foreground'>
              {t('attributeTable.totalFeatures', { count: (data?.pages[0] as any)?.featuresPaginated?.total || 0 })}
            </span>
            {summary && (
              <span className='text-sm text-muted-foreground'>
                <span className='font-medium text-foreground/70'>{t('attributeTable.summary')}</span>{' '}
                {[
                  summary.pointCount > 0 &&
                    t('attributeTable.summaryPoints', { count: summary.pointCount.toLocaleString() }),
                  summary.lineCount > 0 &&
                    t('attributeTable.summaryLines', {
                      count: summary.lineCount.toLocaleString(),
                      length: summary.totalLineLengthM.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                    }),
                  summary.polygonCount > 0 &&
                    t('attributeTable.summaryPolygons', {
                      count: summary.polygonCount.toLocaleString(),
                      area: summary.totalPolygonAreaSqm.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                      perimeter: summary.totalPolygonPerimeterM.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                    }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </div>
        </div>
      </div>
      <PortalTooltip id='attribute-table-serial-tooltip'>{t('attributeTable.sgidTooltip')}</PortalTooltip>
      <BulkEditDrawer
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        featureIds={selection || []}
        layerId={layerId}
        projectId={projectId}
        layers={layers}
      />
      <BulkEditDrawer
        open={!!contextEditFeatureId}
        onClose={() => setContextEditFeatureId(null)}
        featureIds={contextEditFeatureId ? [contextEditFeatureId] : []}
        layerId={layerId}
        projectId={projectId}
        layers={layers}
      />
      {contextMenu && (
        <DropdownMenu
          open
          onOpenChange={(open) => {
            if (!open) setContextMenu(null);
          }}>
          <DropdownMenuContent
            className='whitespace-nowrap'
            style={{
              position: 'fixed',
              left: Math.min(contextMenu.x, window.innerWidth - 160),
              top: Math.min(contextMenu.y, window.innerHeight - 80),
              zIndex: 1000,
            }}>
            <DropdownMenuGroup>
              <DropdownMenuItem
                disabled={jumpingFeatureId === contextMenu.feature.id}
                onClick={() => {
                  handleJumpToFeature(contextMenu.feature.id);
                  setContextMenu(null);
                }}>
                <Crosshair className='size-4' />
                {t('attributeTable.jumpToFeature')}
              </DropdownMenuItem>
              {hasRole('EDITOR') && (
                <DropdownMenuItem
                  onClick={() => {
                    setContextEditFeatureId(contextMenu.feature.id);
                    setContextMenu(null);
                  }}>
                  <Pencil className='size-4' />
                  {t('attributeTable.editFeature')}
                </DropdownMenuItem>
              )}
              {hasRole('ADMIN') && (
                <DropdownMenuItem
                  variant='destructive'
                  onClick={() => {
                    setDeleteConfirmFeature({ id: contextMenu.feature.id });
                    setContextMenu(null);
                  }}>
                  <Trash2 className='size-4' />
                  {t('attributeTable.deleteFeature')}
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <AlertDialog
        open={!!deleteConfirmFeature}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmFeature(null);
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('attributeTable.confirmDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('attributeTable.confirmDeleteFeature')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('attributeTable.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              onClick={() => {
                if (deleteConfirmFeature) {
                  contextDeleteMutation.mutate(deleteConfirmFeature.id);
                }
                setDeleteConfirmFeature(null);
              }}>
              {t('attributeTable.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
